import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import { isAgentAvailable } from './agent-availability'
import { onDesktopWake } from './desktop-wake'

/**
 * Ask the passerelle to refresh the local Claude login when the member comes back — before they
 * type, not after their message has failed.
 *
 * A laptop that slept overnight returns with an expired access token, and `claude auth status` is
 * presence-only: it still reports "signed in" right up until the first run fails. So without this,
 * the way a member discovers their login died is a chat message that took a while to fail.
 *
 * ## The three triggers
 *
 * Mount, `visibilitychange` → visible, and the desktop shell's `onWake` bridge. The third is not
 * redundant: a laptop resuming with the Niobe window already visible and the network already up
 * fires neither of the first two — nothing about the DOM changed. `onDesktopWake` feature-detects
 * the bridge, so an older shell (or the browser, or the test gate's stub) is simply a no-op.
 *
 * ## Why it polls a job instead of just invalidating the status
 *
 * The route answers 202 *before* the CLI has touched the credentials, so invalidating
 * `['agent-status']` on the response re-reads the OLD answer. Nothing would correct it afterwards
 * either: both consumers use `staleTime: 10_000` with no `refetchInterval`, React Query's focus
 * refetch fires on the same `visibilitychange` that triggered the warm-up, and the `onWake` case
 * has no focus event at all. So the warm-up's own job is polled, and the status is invalidated once
 * that job leaves `running` — which is what makes the sign-in banner appear on its own a few
 * seconds later, with no message sent and no second window switch.
 *
 * The passerelle's gate is the real guard against cost (it runs at most one warm-up per 30 min, and
 * only when the token has actually expired); this debounce just stops a member alt-tabbing from
 * generating a request per switch.
 */

/** Client-side floor between two `POST /agent/warmup` calls. The server gate is the real one. */
const WARMUP_DEBOUNCE_MS = 5 * 60_000

export function useAgentWarmup(): void {
  const qc = useQueryClient()
  /** The warm-up job to watch, or null. Held in state (not a ref) because it drives a query. */
  const [jobId, setJobId] = useState<string | null>(null)
  const lastFiredAt = useRef(0)

  const fire = useCallback(() => {
    if (!isAgentAvailable()) return
    const now = Date.now()
    if (now - lastFiredAt.current < WARMUP_DEBOUNCE_MS) return
    lastFiredAt.current = now
    // Fire-and-forget, and deliberately silent: a warm-up is invisible work the member never asked
    // for, so nothing about it — including its failure — belongs on screen. The Promise.resolve()
    // wrapper turns even a synchronous throw into a caught rejection.
    void Promise.resolve()
      .then(() => api.warmupAgent())
      .then((r) => {
        // ran:false means the gate declined (token still valid, an agent job running, too soon).
        // There is nothing to watch and nothing to refresh.
        if (r?.ran && r.jobId) setJobId(r.jobId)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!isAgentAvailable()) return
    fire()
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') fire()
    }
    document.addEventListener('visibilitychange', onVisibility)
    const offWake = onDesktopWake(fire)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      offWake()
    }
  }, [fire])

  const jobQuery = useQuery({
    queryKey: ['agent-job', jobId],
    queryFn: () => api.agentJob(jobId as string),
    enabled: !!jobId,
    refetchInterval: 2000,
  })
  const jobStatus = jobQuery.data?.status

  useEffect(() => {
    if (!jobId || !jobStatus || jobStatus === 'running') return
    // Dropping the id both stops the polling and, next render, disables the query.
    setJobId(null)
    void qc.invalidateQueries({ queryKey: ['agent-status'] })
  }, [jobId, jobStatus, qc])
}
